<template>
    <div>
        <div
            :class="[
                'border-b border-theme-border rounded-lg px-1 flex flex-wrap-col-reverse sm:flex-row transition-all duration-300',
                quantity === 0 && !enabled
                    ? 'bg-theme-hover text-theme-muted opacity-70 line-through border-theme-danger'
                    : ''
            ]"
        >
            <div
                class="lg:w-2/12 md:w-1/4 sm:w-4/12 w-full flex justify-center items-center py-3"
            >
                <a
                    v-if="'images' in reference"
                    class="w-28 h-28 p-0"
                    :href="`/app/products/detail/${reference.productId}?tab=references&reference=${reference.id}`"
                    target="_blank"
                    rel="noopener"
                >
                    <img
                        v-img-loading
                        class="w-full h-full"
                        :src="reference.images[0].url | image"
                        :alt="reference.images[0].alt"
                        loading="lazy"
                    />
                </a>
                <img
                    v-else
                    v-img:group
                    class="w-28 h-28 p-0"
                    :src="$data.$defaultImageUrl"
                    loading="lazy"
                />
            </div>
            <div
                class="lg:w-10/12 md:w-3/4 sm:w-8/12 w-full flex flex-col justify-around py-3 px-1"
            >
                <div
                    class="flex lg:justify-between lg:flex-row flex-col items-center my-2"
                >
                    <div v-if="isRealProduct" class="flex flex-col w-full">
                        <router-link
                            v-if="$ability.hasPermission('inventory:Get')"
                            :to="inventoryUrl"
                            class="btn-a p-0"
                        >
                            <strong>
                                {{ reference.product.name | cut(50) }}&nbsp;
                            </strong>
                        </router-link>
                        <strong v-else>
                            {{ reference.product.name | cut(50) }}&nbsp;
                        </strong>
                        <div>
                            <small>
                                Ref.
                                <router-link
                                    :to="referenceUrl"
                                    class="btn-a p-0"
                                >
                                    ID. {{ reference.id }}
                                    {{
                                        reference.barcode
                                            ? ` Cód. ${reference.barcode}`
                                            : ``
                                    }}
                                </router-link>

                                <fragment
                                    v-if="
                                        reference.additionalConfigurations &&
                                        reference.additionalConfigurations
                                            .length
                                    "
                                >
                                    | Tiene configuraciones extra
                                </fragment>
                                | Prod.
                                <router-link
                                    :to="`/app/products/detail/${reference.productId}`"
                                    class="btn-a p-0"
                                >
                                    ID {{ reference.productId }}
                                    {{
                                        reference.product.sku
                                            ? ` SKU ${reference.product.sku}`
                                            : ""
                                    }}
                                </router-link>
                            </small>
                        </div>
                    </div>
                    <div v-else class="flex flex-col w-full">
                        <span class="p-0">
                            <template v-if="isEditingName">
                                <input
                                    ref="productNameInput"
                                    v-model="editedProductName"
                                    class="form-control form-control-sm text-theme-base"
                                    autofocus
                                    @blur="saveProductName"
                                    @keyup.enter="saveProductName"
                                />
                            </template>
                            <template v-else>
                                <span
                                    style="cursor: pointer"
                                    @click="editProductName"
                                >
                                    {{ reference.product.name }}&nbsp;
                                </span>
                            </template>
                        </span>
                        <small> Servicio / P.A. </small>
                    </div>
                    <div
                        id="actions"
                        class="flex flex-col-reverse md:flex-row justify-end w-full"
                    >
                        <div
                            v-if="enabled"
                            class="flex justify-end items-center"
                        >
                            <custom-tooltip
                                v-if="enabledDiscount && !enabledSalePrice"
                                border
                            >
                                <custom-checkbox
                                    v-model="availableDiscount"
                                    warn
                                    @input="changeAvailableDiscount"
                                />
                                <template #tooltip>
                                    Aplicar Descuento 🤑</template
                                >
                            </custom-tooltip>

                            <quantity-button
                                v-model="quantity"
                                class="mx-2"
                                max="100"
                                min="1"
                                inline
                                @input="changeQuantity"
                            />

                            <app-button danger circle icon @click="remove">
                                🗑
                            </app-button>
                        </div>
                        <div
                            class="flex justify-center justify-content-md-end items-center"
                        >
                            <tooltip-button
                                v-if="enabledDetails"
                                icon="👀"
                                message="Ver detalles"
                                @click="openDetails"
                            />
                            <fragment v-if="reference.productId">
                                <tooltip-redirect
                                    v-if="
                                        $ability.hasPermission('inventory:Get')
                                    "
                                    icon="🧮"
                                    open-new-tab
                                    message="Ver inventario"
                                    :to="inventoryUrl"
                                />
                                <tooltip-redirect
                                    icon="🛍"
                                    open-new-tab
                                    message="Ver referencia"
                                    :to="referenceUrl"
                                />
                            </fragment>
                        </div>
                    </div>
                </div>

                <div
                    class="flex flex-col lg:flex-row flex-wrap lg:justify-between"
                >
                    <div
                        v-if="reference.color"
                        class="flex lg:flex-col justify-between pr-2"
                    >
                        <strong>Color </strong>
                        <div class="flex items-center">
                            <span class="text-base capitalize">
                                {{ reference.color }}
                            </span>
                            <div
                                class="ml-1 w-5 h-5 rounded-full border border-theme-border"
                                :style="{
                                    'background-color': reference.colorRef
                                }"
                            ></div>
                        </div>
                    </div>
                    <div
                        v-if="reference.size"
                        class="flex lg:flex-col justify-between pr-2"
                    >
                        <strong>Tamaño </strong>
                        <span class="text-base capitalize">
                            {{ reference.size }}
                        </span>
                    </div>
                    <div
                        v-if="reference.customReferenceValue"
                        class="flex lg:flex-col justify-between pr-2"
                    >
                        <strong>{{
                            reference.product &&
                            reference.product.customReferenceTitle
                                ? reference.product.customReferenceTitle
                                : "Variante"
                        }}</strong>
                        <span class="text-base capitalize">
                            {{ reference.customReferenceValue }}
                        </span>
                    </div>
                    <div class="flex lg:flex-col justify-between pr-2">
                        <strong>Cantidad </strong>
                        <span
                            class="text-base"
                            :class="{
                                'line-through font-bold': quantity === 0
                            }"
                        >
                            {{ quantity }}
                        </span>
                    </div>
                    <div class="flex lg:flex-col justify-between pr-2">
                        <strong>$ Unidad </strong>
                        <div class="flex">
                            <span v-if="wasAppliedDiscount" class="text-base">
                                {{ lineSalePrice | money }}
                            </span>
                            <span
                                class="text-base"
                                :class="{
                                    'line-through ml-2 text-xs':
                                        wasAppliedDiscount
                                }"
                            >
                                {{ reference.priceOffer | money }}
                            </span>
                        </div>
                    </div>
                    <div
                        v-if="wasAppliedDiscount && !enabledSalePrice"
                        class="flex lg:flex-col justify-between pr-2"
                    >
                        <strong>$ Descuento u. </strong>
                        <span class="text-base">
                            {{ reference.appliedDiscountPerReference | money }}
                        </span>
                    </div>
                    <div
                        v-if="
                            reference.cost && Number.isInteger(reference.cost)
                        "
                        class="flex lg:flex-col justify-between pr-2"
                    >
                        <strong>Costo unidad </strong>
                        <span class="text-base">
                            {{ reference.cost | money }}
                        </span>
                    </div>
                    <div class="w-full mt-2 flex pr-2">
                        <span class="text-theme-primary font-bold">
                            $ Productos: &nbsp;
                        </span>
                        <span
                            class="font-bold h5 text-base"
                            :class="{
                                'line-through ': quantity === 0
                            }"
                        >
                            {{ (lineSalePrice * quantity) | money }}
                        </span>
                    </div>
                </div>

                <div v-if="enabledSalePrice && enabled" class="mt-5 pr-2">
                    <input-money-component
                        v-model="salePrice"
                        enabled
                        label="Precio de venta"
                        icon="💲"
                        :message-error="salePriceError"
                        @blur="blurSalePrice"
                        @focus="focusInput"
                    />
                </div>

                <div v-else-if="availableDiscount && enabled" class="mt-5 pr-2">
                    <input-money-component
                        v-model="appliedDiscountPerReference"
                        enabled
                        label="Descuento neto por referencia"
                        icon="💲"
                        :message-error="
                            appliedDiscountPerReference >
                            reference.priceOffer * 0.6
                                ? 'Descuento no debe exceder el 60% del valor de la referencia'
                                : ''
                        "
                        @blur="blurAppliedDiscountPerReference"
                        @focus="focusInput"
                    />
                </div>
            </div>
        </div>

        <app-dialog v-model="isOpenDetails">
            <template #header>
                <h2 class="text-lg">
                    {{ reference.product.name }}
                </h2>
            </template>
            <div>
                <div class="flex flex-wrap">
                    <div class="flex flex-col w-full md:w-1/2">
                        <strong>Color </strong>
                        <div class="flex items-center">
                            <span class="capitalize">
                                {{ reference.color }}
                            </span>
                            <div
                                class="ml-1 w-5 h-5 rounded-full border border-theme-border"
                                :style="{
                                    'background-color': reference.colorRef
                                }"
                            ></div>
                        </div>
                    </div>
                    <div class="flex flex-col w-full md:w-1/2">
                        <strong>Tamaño/Talla/Valor </strong>
                        <span class="capitalize">
                            {{ reference.size }}
                        </span>
                    </div>
                    <div
                        v-if="reference.customReferenceValue"
                        class="flex flex-col w-full md:w-1/2"
                    >
                        <strong>{{
                            reference.product &&
                            reference.product.customReferenceTitle
                                ? reference.product.customReferenceTitle
                                : "Variante"
                        }}</strong>
                        <span class="capitalize">
                            {{ reference.customReferenceValue }}
                        </span>
                    </div>
                    <div class="flex flex-col w-full md:w-1/2">
                        <strong>Cantidad </strong>
                        <span>
                            {{ quantity }}
                        </span>
                    </div>
                    <div class="flex flex-col w-full md:w-1/2">
                        <strong>Precio por unidad </strong>
                        <span>
                            {{ reference.priceOffer | currency }}
                        </span>
                    </div>
                    <div
                        v-if="enabledSalePrice"
                        class="flex flex-col w-full md:w-1/2"
                    >
                        <strong>Precio de venta por unidad </strong>
                        <span>
                            {{ lineSalePrice | currency }}
                        </span>
                    </div>
                    <div v-else class="flex flex-col w-full md:w-1/2">
                        <strong>Valor de descuento por unidad </strong>
                        <span>
                            {{
                                reference.appliedDiscountPerReference | currency
                            }}
                        </span>
                    </div>
                    <div class="flex flex-col w-full md:w-1/2">
                        <strong>
                            Costo unidad en el momento de la orden
                        </strong>
                        <span>
                            {{ reference.cost | currency }}
                        </span>
                    </div>
                    <div class="flex flex-col w-full md:w-1/2">
                        <strong> Precio de referencias: </strong>
                        <span>
                            {{ (lineSalePrice * quantity) | currency }}
                        </span>
                    </div>
                    <div class="w-full">
                        <div
                            v-if="
                                reference.additionalConfigurations &&
                                reference.additionalConfigurations.length
                            "
                        ></div>
                        <strong v-else class="mt-3">
                            No tiene configuraciones adicionales
                        </strong>
                        <div
                            v-if="
                                reference.additionalConfigurations &&
                                reference.additionalConfigurations.length
                            "
                        >
                            <strong class="mt-3"
                                >Configuraciones adicionales:</strong
                            >
                            <div
                                v-for="config in reference.additionalConfigurations"
                                :key="config.id"
                                class="mt-2"
                            >
                                <div class="flex flex-col">
                                    <strong>{{ config.title }}</strong>
                                    <div
                                        v-if="
                                            [
                                                'imageChoice',
                                                'imageUpload'
                                            ].includes(config.type)
                                        "
                                        class="mt-1"
                                    >
                                        <img
                                            v-img:group
                                            :src="config.value"
                                            class="w-28 h-28"
                                            loading="lazy"
                                            :alt="config.title"
                                        />
                                    </div>
                                    <span v-else class="mt-1">
                                        {{ config.value }}
                                    </span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </app-dialog>
    </div>
</template>

<script>
import { mapGetters } from "vuex";

import { DEFAULT_IMAGE_URL } from "@/common/constants";
import {
    resolveLineSalePrice,
    validateLineSalePrice
} from "@/common/lib/order-line-price";

import AppDialog from "@/components/utils/AppDialog.vue";
import InputMoneyComponent from "@/components/utils/InputMoneyComponent.vue";
import QuantityButton from "@/components/utils/QuantityButton.vue";
import TooltipButton from "@/components/utils/TooltipButton.vue";
import TooltipRedirect from "@/components/utils/TooltipRedirect.vue";

export default {
    name: "OrderReferenceTableItem",
    components: {
        AppDialog,
        InputMoneyComponent,
        TooltipButton,
        TooltipRedirect,
        QuantityButton
    },
    props: {
        reference: {
            type: Object,
            required: true,
            default: () => ({
                id: 20,
                size: "pequeño",
                color: "rojo",
                colorRef: "#f00",
                quantity: 1,
                price: 10000,
                priceOffer: 10000,
                appliedDiscountPerReference: 0,
                images: [],
                additionalConfigurations: [
                    {
                        id: "front-images",
                        type: "imageChoice",
                        title: "Imagen de frente",
                        value: "https://titin-pro-public-images-bucket.s3.us-east-2.amazonaws.com/large/1703802601.png",
                        requestedByEntity: "reference",
                        additionalCostByUnit: 0,
                        sectionWhereWasFilled: "cartButton"
                    }
                ],
                product: {
                    id: "",
                    name: "",
                    summary: ""
                }
            })
        },
        merchant: {
            default: () => null,
            type: Object, //{merchantId:string,merchantType:string}
            required: false
        },
        enabled: {
            default: () => false,
            type: Boolean,
            required: false
        },
        enabledDiscount: {
            type: Boolean,
            required: false,
            default: () => false
        },
        enabledDetails: {
            type: Boolean,
            required: false,
            default: () => true
        },
        // pedido-domicilio-portal: "Precio de venta" por unidad en lugar del
        // descuento.
        enabledSalePrice: {
            type: Boolean,
            required: false,
            default: () => false
        },
        allowAboveCatalog: {
            type: Boolean,
            required: false,
            default: () => false
        }
    },
    emits: ["update", "remove", "focus"],
    data: () => ({
        isEditingName: false,
        editedProductName: "",
        quantity: 0,
        appliedDiscountPerReference: 0,
        salePrice: 0,
        inventories: [],
        availableDiscount: false,
        isOpenDetails: false,
        stockActive: 0,
        $defaultImageUrl: DEFAULT_IMAGE_URL
    }),
    computed: {
        ...mapGetters("control", ["backgroundColor"]),
        inventoryUrl() {
            return this.merchant
                ? `/app/inventories/${this.merchant.merchantType}/${this.merchant.merchantId}/detail/${this.reference.id}`
                : `/app/inventories/detail/${this.reference.id}`;
        },
        referenceUrl() {
            return `/app/products/detail/${this.reference.productId}?reference=${this.reference.id}&tab=references`;
        },
        isRealProduct() {
            return (
                "id" in this.reference.product &&
                !!this.reference.product.id &&
                !!this.reference.id
            );
        },
        lineSalePrice() {
            return resolveLineSalePrice(this.reference);
        },
        wasAppliedDiscount() {
            return this.enabledSalePrice
                ? this.lineSalePrice !== Number(this.reference.priceOffer)
                : this.reference.appliedDiscountPerReference > 0;
        },
        salePriceError() {
            return validateLineSalePrice({
                salePrice: this.salePrice,
                priceOffer: this.reference.priceOffer,
                allowAboveCatalog: this.allowAboveCatalog
            });
        }
    },
    watch: {
        reference(value) {
            this.setInput(value);
        }
    },
    mounted() {
        this.setInput(this.reference);
    },
    methods: {
        openDetails() {
            this.isOpenDetails = true;
        },
        goToMainPage(slug) {
            if (this.reference.active) {
                window.open(`${this.$publicUrl}/${slug}`, "_blank").focus();
            }
        },
        remove() {
            this.$emit("remove", { id: this.reference.id });
        },
        blurAppliedDiscountPerReference() {
            this.$emit("update", {
                ...this.reference,
                quantity: this.quantity,
                appliedDiscountPerReference: this.appliedDiscountPerReference
            });
        },
        blurSalePrice() {
            this.$emit("update", {
                ...this.reference,
                quantity: this.quantity,
                salePrice: this.salePrice
            });
        },
        focusInput() {
            this.$emit("focus");
        },
        setInput(value) {
            this.quantity = Number(value.quantity);
            this.salePrice = resolveLineSalePrice(value);
            this.appliedDiscountPerReference =
                Number(value.appliedDiscountPerReference) || 0;
            this.availableDiscount = this.appliedDiscountPerReference > 0;
            this.editedProductName = value.product.name;
        },
        changeQuantity(value) {
            this.$emit("update", {
                ...this.reference,
                quantity: value,
                appliedDiscountPerReference: this.appliedDiscountPerReference,
                ...(this.enabledSalePrice ? { salePrice: this.salePrice } : {})
            });
        },
        changeAvailableDiscount(value) {
            if (!value) {
                this.appliedDiscountPerReference = 0;
                this.blurAppliedDiscountPerReference();
            }
        },
        editProductName() {
            if (this.enabled) {
                this.isEditingName = true;
            }
        },
        saveProductName() {
            if (this.editedProductName.trim() !== "") {
                this.$emit("update", {
                    ...this.reference,
                    product: {
                        ...this.reference.product,
                        name: this.editedProductName
                    }
                });
            }
            this.isEditingName = false;
        }
    }
};
</script>
